import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Sun, Calendar, DollarSign, Cake, FileWarning, Users,
  AlertTriangle, Clock, ChevronRight, Loader2, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MorningBriefing({ orgId }) {
  const { isDark } = useTheme();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  const fetchBriefing = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/briefing/daily/${orgId}`);
      setBriefing(data);
    } catch (err) {
      console.error('Briefing fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchBriefing(); }, [fetchBriefing]);

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (!briefing) return null;

  const { summary } = briefing;
  const hasAlerts = summary.overdue_payments > 0 || summary.pending_requests > 0 || summary.expiring_documents > 0;

  return (
    <div className="space-y-4" data-testid="morning-briefing">
      {/* Greeting Card */}
      <Card className={`${isDark ? 'bg-gradient-to-r from-amber-900/30 to-slate-800/50 border-amber-700/50' : 'bg-gradient-to-r from-amber-50 to-white border-amber-200'}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-500/20">
                <Sun className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Morning Briefing</h2>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{briefing.greeting}</p>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={fetchBriefing} className={isDark ? 'text-slate-400' : 'text-slate-500'} data-testid="refresh-briefing">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Summary Badges */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge className={`${summary.upcoming_games > 0 ? 'bg-blue-600' : isDark ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'}`}>
              <Calendar className="h-3 w-3 mr-1" />{summary.upcoming_games} games this week
            </Badge>
            <Badge className={`${summary.overdue_payments > 0 ? 'bg-red-600' : isDark ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'}`}>
              <DollarSign className="h-3 w-3 mr-1" />{summary.overdue_payments} overdue (${summary.overdue_total})
            </Badge>
            <Badge className={`${summary.pending_requests > 0 ? 'bg-amber-600' : isDark ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'}`}>
              <Clock className="h-3 w-3 mr-1" />{summary.pending_requests} pending requests
            </Badge>
            <Badge className={`${summary.birthdays_this_week > 0 ? 'bg-pink-600' : isDark ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'}`}>
              <Cake className="h-3 w-3 mr-1" />{summary.birthdays_this_week} birthdays
            </Badge>
            {summary.expiring_documents > 0 && (
              <Badge className="bg-orange-600">
                <FileWarning className="h-3 w-3 mr-1" />{summary.expiring_documents} docs expiring
              </Badge>
            )}
            {summary.unfilled_volunteer_slots > 0 && (
              <Badge className={isDark ? 'bg-slate-700' : 'bg-slate-200 text-slate-700'}>
                <Users className="h-3 w-3 mr-1" />{summary.unfilled_volunteer_slots} volunteer slots open
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alerts Section */}
      {hasAlerts && (
        <Card className={`border ${isDark ? 'bg-red-900/10 border-red-800/50' : 'bg-red-50 border-red-200'}`}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle('alerts')}>
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-red-400' : 'text-red-700'}`}>
              <AlertTriangle className="h-4 w-4" />Action Required
              <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${collapsed.alerts ? '' : 'rotate-90'}`} />
            </CardTitle>
          </CardHeader>
          {!collapsed.alerts && (
            <CardContent className="pt-0 space-y-2">
              {briefing.overdue_payments.map((p, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-100/50'}`} data-testid={`overdue-${i}`}>
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {p.family_name} — ${p.amount_due} due {p.due_date?.split('T')[0]}
                  </span>
                  <Badge className="bg-red-600 text-xs">Overdue</Badge>
                </div>
              ))}
              {briefing.pending_requests.map((r, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-amber-900/20' : 'bg-amber-100/50'}`} data-testid={`pending-req-${i}`}>
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {r.requesting_team} wants to schedule {r.date?.split('T')[0]}
                  </span>
                  <Badge className="bg-amber-600 text-xs">Pending</Badge>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Upcoming Games */}
      {briefing.upcoming_games.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle('games')}>
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Calendar className="h-4 w-4 text-blue-500" />
              Upcoming Games ({briefing.upcoming_games.length})
              <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${collapsed.games ? '' : 'rotate-90'}`} />
            </CardTitle>
          </CardHeader>
          {!collapsed.games && (
            <CardContent className="pt-0 space-y-2">
              {briefing.upcoming_games.map((g, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                      {g.home_team} vs {g.away_team}
                    </span>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{g.venue} · {g.date?.split('T')[0]}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Birthdays */}
      {briefing.birthday_alerts.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle('bdays')}>
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Cake className="h-4 w-4 text-pink-500" />
              Birthdays This Week ({briefing.birthday_alerts.length})
              <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${collapsed.bdays ? '' : 'rotate-90'}`} />
            </CardTitle>
          </CardHeader>
          {!collapsed.bdays && (
            <CardContent className="pt-0 space-y-2">
              {briefing.birthday_alerts.map((b, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{b.name}</span>
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Turning {b.age_turning} {b.days_until === 0 ? 'today!' : `in ${b.days_until} day${b.days_until > 1 ? 's' : ''}`}
                  </span>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Unfilled Volunteer Tasks */}
      {briefing.unfilled_tasks.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle('tasks')}>
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Users className="h-4 w-4 text-green-500" />
              Volunteer Openings ({briefing.unfilled_tasks.length})
              <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${collapsed.tasks ? '' : 'rotate-90'}`} />
            </CardTitle>
          </CardHeader>
          {!collapsed.tasks && (
            <CardContent className="pt-0 space-y-2">
              {briefing.unfilled_tasks.map((t, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.title}</span>
                  <Badge className={isDark ? 'bg-slate-600' : 'bg-slate-200 text-slate-700'}>{t.slots_remaining} slots</Badge>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
